import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  History, BookOpenCheck, Trophy, Flame, ArrowUpRight, PencilRuler, Gift, ChevronRight,
} from "lucide-react";

/**
 * WeeklyDigestCard — dashboard card showing a founder's last-7-days activity.
 * Consumes GET /api/digest/preview. Silently hides on 401 / network error.
 */
export default function WeeklyDigestCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/digest/preview")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;

  const { totals, streak, blueprint, completed_modules, passed_quizzes } = data;
  const nothingHappened = !totals.modules && !totals.quizzes && !totals.days_active && !totals.referrals && !blueprint.updated_this_week;

  const items = [
    { key: "modules",   icon: BookOpenCheck, label: "Modules",       value: totals.modules,   sub: totals.modules === 1 ? "completed" : "completed" },
    { key: "quizzes",   icon: Trophy,        label: "Quizzes",       value: totals.quizzes,   sub: totals.quizzes === 1 ? "passed" : "passed" },
    { key: "streak",    icon: Flame,         label: "Active days",   value: totals.days_active, sub: "of 7" },
    { key: "referrals", icon: Gift,          label: "Referrals",     value: totals.referrals, sub: "joined" },
  ];

  return (
    <div
      id="digest"
      data-testid="dashboard-weekly-digest"
      className="rounded-2xl p-6 bg-gradient-to-br from-shell-side to-shell-side/95 text-white relative overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="chip-amber inline-flex items-center gap-1 !bg-shell-accent !text-shell-side">
            <History className="w-3 h-3" /> Your week in review
          </p>
          <h3 className="font-display text-2xl mt-3">
            {nothingHappened ? "A quiet week — let's change that" : "Nice moves this week"}
          </h3>
          <p className="text-sm text-white/80 mt-1 max-w-lg">
            {nothingHappened
              ? "You didn't tick anything off in the last 7 days. Pick a module below or open your Launch Blueprint to move the needle."
              : "A snapshot of what you learned, shipped, and unlocked in the last 7 days."}
          </p>
        </div>
        <Link
          to="/account/notifications"
          data-testid="digest-manage-link"
          className="text-[11px] font-semibold text-white/70 hover:text-white inline-flex items-center gap-1 transition"
        >
          Digest settings <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="digest-totals">
        {items.map((it) => (
          <div
            key={it.key}
            data-testid={`digest-tile-${it.key}`}
            className="rounded-xl bg-white/10 border border-white/10 p-3"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-white/70">
              <it.icon className="w-3 h-3" /> {it.label}
            </div>
            <p className="font-display text-3xl mt-1 leading-none">{it.value}</p>
            <p className="text-[11px] text-white/60 mt-1">{it.sub}</p>
          </div>
        ))}
      </div>

      {/* Streak + blueprint chips */}
      <div className="mt-4 flex items-center gap-2 flex-wrap text-[12px]">
        <span
          data-testid="digest-streak-chip"
          className="inline-flex items-center gap-1.5 rounded-full bg-shell-accent/25 border border-shell-accent/40 px-3 py-1"
        >
          <Flame className="w-3.5 h-3.5 text-shell-accent" />
          {streak.current}-day streak <span className="text-white/60 ml-1">· best {streak.best}</span>
        </span>
        <span
          data-testid="digest-blueprint-chip"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${
            blueprint.updated_this_week
              ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-100"
              : "bg-white/5 border-white/10 text-white/70"
          }`}
        >
          <PencilRuler className="w-3.5 h-3.5" />
          Blueprint: {blueprint.updated_this_week ? "edited this week" : "no edits this week"}
          <span className="text-white/60 ml-1">· {blueprint.field_count} fields</span>
        </span>
      </div>

      {/* Recent detail */}
      {(completed_modules.length > 0 || passed_quizzes.length > 0) && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-[0.14em] text-white/60 mb-2">Highlights</p>
          <ul className="space-y-1 text-[12.5px]" data-testid="digest-highlights">
            {completed_modules.slice(0, 2).map((m) => (
              <li key={`m-${m.slug}`} className="flex items-center gap-2">
                <BookOpenCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                <span>Completed <code className="text-white/80">{m.slug}</code></span>
              </li>
            ))}
            {passed_quizzes.slice(0, 2).map((q) => (
              <li key={`q-${q.slug}`} className="flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-shell-accent shrink-0" />
                <span>Passed quiz for <code className="text-white/80">{q.slug}</code>{q.score != null ? ` · ${q.score}%` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Link
          to="/modules"
          data-testid="digest-cta-modules"
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-shell-side px-4 py-2 text-xs font-semibold hover:bg-white/90 transition"
        >
          {nothingHappened ? "Pick this week's module" : "Keep the momentum"}
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/blueprint"
          data-testid="digest-cta-blueprint"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-4 py-2 text-xs font-semibold hover:border-white/60 transition text-white"
        >
          Open Launch Blueprint
        </Link>
      </div>
    </div>
  );
}
